/**
 * The vocabulary every Workshop tool uses to talk about a GTA V resource.
 *
 * Five surfaces analyse the same files — PackBench before an export, Easy Meta
 * while generating one, Code Smith and Code Visualizer over a repository, and the
 * desktop app over a folder on disk. Before this module they each had their own
 * idea of what a problem was, which meant the same broken `handlingId` produced
 * four different sentences and only one of them said what to do about it.
 *
 * So the shape is fixed here, and the rule is:
 *
 *   Every finding answers three questions.
 *   1. What is wrong?              → `title`
 *   2. Why does it matter in game? → `detail`, first half
 *   3. What should I do next?      → `detail`, second half, and `fix` when we can
 *
 * A finding that cannot answer the second question is not worth reporting. That
 * is not style advice: the reason linters get ignored is that they report things
 * the reader cannot connect to anything they care about, and in this domain the
 * consequence is always concrete — the vehicle spawns without mods, the texture
 * never sharpens, the resource does not start.
 *
 * Nothing in this file has a runtime cost. It is types and the two or three
 * constants that give them meaning.
 */

// ─── Inputs ──────────────────────────────────────────────────────────────────

/**
 * One file in the folder being analysed.
 *
 * Structurally identical to `ResourceFile` in `desktop/src/lib/resourceScan.ts`,
 * on purpose: the desktop collector already produces this and should not have to
 * translate.
 */
export type GtaFile = {
  /** Path relative to the resource root, `/`-separated. */
  path: string;
  name: string;
  /** Lower-cased, no leading dot. */
  extension: string;
  size: number;
};

/**
 * Everything the analyzers get, and the boundary that keeps them pure.
 *
 * The caller reads files; the analyzers only ever see this. That is what lets
 * the same rules run in a browser over an uploaded zip, in the desktop app over
 * a folder, and in a test over a literal object.
 */
export type GtaResourceInput = {
  /** The resource's own name — its folder name, usually. */
  name: string;
  /**
   * What makes this resource *a different thing* from the one next to it.
   *
   * `name` cannot do that job, and the reason is the single most valuable
   * finding this layer produces. Two vendor zips that both ship `police/` arrive
   * as two resources both called `police`, and that duplicate name is not a
   * collision in the input — it is the finding. A server cannot start both, and
   * one of them silently wins. So the name has to stay duplicated all the way to
   * the report, which means it cannot also be the key that tells them apart:
   * keying on it is what folds the two packs into one, drops the second
   * archive's stream assets on the way in, and returns a clean bill of health
   * for the exact input the tool was opened to diagnose.
   *
   * `identity` is therefore the unique one — the drop-relative root,
   * `vendor-b.zip/police` — and `name` stays the thing FiveM would call the
   * resource and the thing a report shows. Optional because a single-resource
   * caller has nothing to disambiguate from; every consumer falls back to `name`
   * when it is absent, which is what keeps the pre-existing call shape correct
   * rather than merely compiling.
   */
  identity?: string;
  /**
   * The archive or folder pick this resource came out of, for display.
   *
   * Separate from `identity` because a user cannot act on a key. "police and
   * police each ship police.yft" names no file anyone can open; "police (from
   * vendor-a.zip) and police (from vendor-b.zip)" names two. This is the half
   * that makes a cross-resource finding actionable, and it is the half a
   * report's per-resource heading and CSV column print.
   */
  origin?: string;
  files: GtaFile[];
  /**
   * Decoded text of the files worth reading: manifests, `.meta`, `.xml`, `.lua`.
   * Keyed by lower-cased relative path. Anything absent is simply not analysed,
   * and every rule that depends on a file says so rather than assuming.
   */
  texts: Map<string, string>;
  /**
   * Pairs of files the caller compared byte-for-byte, by lower-cased path.
   * Reading two 10 MB models to compare them is the caller's call to make, so
   * the analyzer asks for the answer rather than the bytes.
   */
  identicalPairs?: [string, string][];
};

/**
 * Several resources at once, for the checks that only exist between them.
 *
 * A stream basename collision is invisible from inside either resource: both are
 * correct, and the server loads one of them. Only a view across the set can see
 * it, which is why this is a separate input shape rather than an option.
 */
export type GtaMultiResourceInput = {
  resources: GtaResourceInput[];
};

// ─── Findings ────────────────────────────────────────────────────────────────

export type GtaSeverity = "error" | "warning" | "info";

/**
 * Every diagnostic this layer can produce.
 *
 * Namespaced by subject rather than by severity, because severity is a property
 * of an occurrence and the subject is not: the same `stream.basename.duplicate`
 * is a warning inside one pack and an error across two resources that both ship.
 *
 * Codes are permanent. A rule may change its wording, its severity or its
 * detection entirely, but the code is what a saved report, a CI baseline or a
 * user's muted-rule list refers to, so retiring one means retiring it by name.
 */
export type GtaFindingCode =
  // Manifest
  | "manifest.missing"
  | "manifest.legacy"
  | "manifest.fx_version.missing"
  | "manifest.fx_version.unknown"
  | "manifest.game.missing"
  | "manifest.entry.duplicate"
  | "manifest.path.missing"
  | "manifest.path.case_mismatch"
  | "manifest.data_file.missing"
  | "manifest.data_file.not_exported"
  | "manifest.data_file.unknown_type"
  | "manifest.data_file.wrong_root"
  | "manifest.data_file.double_registered"
  | "manifest.meta.not_registered"
  | "manifest.nested_resource"
  // Dependencies
  | "dependency.missing"
  | "dependency.external"
  | "dependency.system"
  | "dependency.inferred"
  | "dependency.cycle"
  // Vehicles
  | "vehicle.model.missing"
  | "vehicle.model.invalid_chars"
  | "vehicle.model.duplicate"
  | "vehicle.model.vanilla_collision"
  | "vehicle.model.file_missing"
  | "vehicle.handling.missing"
  | "vehicle.handling.duplicate"
  | "vehicle.handling.unused"
  | "vehicle.audio.empty"
  | "vehicle.audio.invalid_chars"
  | "vehicle.game_name.missing"
  | "vehicle.game_name.unresolved"
  | "vehicle.txd.missing"
  | "vehicle.txd.file_missing"
  | "vehicle.layout.missing"
  | "vehicle.modkit.missing"
  | "vehicle.modkit.unverified"
  | "vehicle.modkit.duplicate_id"
  | "vehicle.modkit.stock_id"
  | "vehicle.siren.missing"
  | "vehicle.light.missing"
  | "vehicle.variation.orphan"
  | "vehicle.xml.invalid"
  | "vehicle.xml.unknown_kind"
  // server.cfg
  //
  // About the folder, not about a resource: these are the faults that only exist
  // once somebody has written down what starts and in what order. A resource can
  // be perfect and still never start, and nothing inside it can tell you that.
  | "cfg.ensure.order"
  | "cfg.ensure.cycle"
  | "cfg.ensure.duplicate"
  | "cfg.ensure.missing"
  | "cfg.ensure.unstarted"
  | "cfg.ensure.category"
  | "cfg.name.space"
  | "cfg.onesync.maxclients"
  | "cfg.license.placeholder"
  | "cfg.build.stale"
  | "cfg.db.missing"
  // Stream assets
  | "stream.basename.duplicate"
  | "stream.basename.vanilla_override"
  | "stream.path.duplicate"
  | "stream.asset.orphan"
  // Stream budget
  //
  // About bytes, not correctness: nothing here means the pack is broken, it
  // means the pack costs more to download than it needs to. Kept apart from the
  // `stream.*` block on purpose — a duplicate *name* is a collision the server
  // resolves by picking one, and a duplicate *file* under two names is waste
  // the server faithfully ships twice.
  | "budget.file.oversize"
  | "budget.resource.total"
  | "budget.resource.count"
  | "budget.extension.total"
  | "budget.texture.share"
  | "budget.duplicate"
  | "budget.lod.identical"
  // carcols ids
  //
  // About numbers claimed across `carcols.meta` and `carvariations.meta`, from
  // `carcols.ts`. Kept apart from `vehicle.modkit.*`, which judges one vehicle's
  // own record: these judge the id pools a whole scan shares, and three of them
  // — `siren`, `light`, `modkit` — are separate numeric namespaces, so each pool
  // gets its own duplicate and stock code rather than one code with a kind in
  // the prose.
  | "carcols.id.out_of_range"
  | "carcols.id.sentinel"
  | "carcols.kit.dangling"
  | "carcols.siren.dangling"
  | "carcols.light.dangling"
  | "carcols.xml.unscannable"
  | "carcols.siren.over_limit"
  | "carcols.modkit.duplicate_id"
  | "carcols.modkit.stock_id"
  | "carcols.siren.duplicate_id"
  | "carcols.siren.stock_id"
  | "carcols.light.duplicate_id"
  | "carcols.light.stock_id";

/** What a finding is about, so a UI can group by subject instead of by file. */
/**
 * `siren` and `light` are their own kinds rather than riding on `modkit`, and
 * the reason is `groupByEntity` in `findings.ts`: it keys a group on
 * `kind + id`, so two entities that share a kind and a number are one group. A
 * mod kit id, a siren id and a light id are three *separate* numeric namespaces
 * — sirens run 1–254 and mod kits well past that, so a low custom kit id sharing
 * a number with a siren profile is ordinary, not a coincidence — and folding
 * them onto one kind made an unrelated siren collision render under a heading
 * reading "Mod kit 100", inside the same group as the kit problem, undercounting
 * how many distinct things were actually broken.
 */
export type GtaEntityKind =
  | "resource"
  | "vehicle"
  | "manifest"
  | "modkit"
  | "siren"
  | "light"
  | "stream_asset"
  | "dependency";

export type GtaEntity = {
  kind: GtaEntityKind;
  /** Stable within a report: a model name, a resource name, a kit id. */
  id: string;
};

/**
 * Cross-cutting properties a UI sorts, filters or badges on.
 *
 * `emergency-impact` is the one that earns its keep: it is what puts the broken
 * police car above the broken sedan without inventing a fourth severity level.
 */
export type GtaFindingTag =
  | "emergency-impact"
  | "vanilla"
  | "external"
  | "autofixable"
  | "inferred";

/**
 * A byte range in a source file, plus the human coordinates for it.
 *
 * Offsets are what a patch needs; line and column are what a person needs. Both
 * are optional because plenty of findings are about a file as a whole, and a
 * made-up line number is worse than none.
 */
export type GtaSourceSpan = {
  file?: string;
  line?: number;
  column?: number;
  byteStart?: number;
  byteEnd?: number;
};

/**
 * A single edit, expressed so that applying it can be verified.
 *
 * `expect` is the point of the whole shape. An offset alone is a promise about a
 * file that may have changed since it was read; an offset plus the text that is
 * supposed to be there is a promise that can be checked, and refused.
 */
export type GtaTextEdit = {
  file: string;
  start: number;
  end: number;
  /** The text that must currently occupy `[start, end)`, verbatim or by pattern. */
  expect: RegExp | string;
  replacement: string;
};

export type GtaFix = {
  /** Imperative, and specific: "Add data_file 'CARCOLS_FILE' 'carcols.meta'". */
  label: string;
  /**
   * `high` means we know the edit is right and it can be offered as one click.
   * `medium` means it is right if the guess about intent is right. `low` means
   * show it as a suggestion and let the user decide.
   */
  confidence: "high" | "medium" | "low";
  edits?: GtaTextEdit[];
};

export type GtaFinding = GtaSourceSpan & {
  code: GtaFindingCode;
  severity: GtaSeverity;
  /** One line, no trailing period, names the thing that is wrong. */
  title: string;
  /** What it means in game, then what to do. Full sentences. */
  detail: string;
  entity?: GtaEntity;
  tags?: GtaFindingTag[];
  fix?: GtaFix;
};

// ─── Manifests ───────────────────────────────────────────────────────────────

/** Every manifest key this layer understands, in the form the file writes it. */
export type ManifestEntryKind =
  | "fx_version"
  | "game"
  | "client_script"
  | "server_script"
  | "shared_script"
  | "file"
  | "ui_page"
  | "data_file"
  | "dependency"
  | "provide"
  | "export"
  | "server_export"
  | "escrow_ignore"
  | "lua54"
  | "author"
  | "description"
  | "version"
  | "this_is_a_map"
  | "server_only"
  | "unknown";

/**
 * One value a manifest declared, with where it said it.
 *
 * The span is not a nicety. "Your manifest lists a file that is not there" is a
 * different message from "line 14 lists a file that is not there", and only the
 * second one can offer to fix it.
 */
export type ManifestEntry = {
  kind: ManifestEntryKind;
  /** The raw key as written, so `client_scripts` and `client_script` both survive. */
  key: string;
  value: string;
  /** For `data_file`, the type string; for `provide`, unused. */
  qualifier?: string;
  span: GtaSourceSpan;
};

export type ParsedManifest = {
  /** `fxmanifest.lua` or `__resource.lua`, as found. */
  file: string;
  /** The pre-2019 form: `__resource.lua` or a `resource_manifest_version` line. */
  legacy: boolean;
  fxVersion: string | null;
  games: string[];
  entries: ManifestEntry[];
  /**
   * Constraints such as `/server:6683` or `/gameBuild:3095`, which appear in the
   * same `dependencies` block as real resource names and are not resources at
   * all. Reading them as dependencies is how a correct manifest gets reported as
   * depending on a resource called `/server:6683`.
   */
  constraints: ManifestConstraint[];
  /** Anything the parser saw and did not understand, for honesty in the report. */
  unparsed: string[];
};

export type ManifestConstraint = {
  /** `server`, `gameBuild`, `policy`, `onesync`, `native`… */
  kind: string;
  /** The raw text after the colon: `6683`, `3095`, `subscription`. */
  value: string;
  raw: string;
  span: GtaSourceSpan;
};

/** The three `fx_version` names FiveM accepts, newest last. */
export const FX_VERSIONS: readonly string[] = ["adamant", "bodacious", "cerulean"];

// ─── Dependencies ────────────────────────────────────────────────────────────

/**
 * What we were able to say about a name in a `dependencies` block.
 *
 * `missing` is the only one that is unambiguously a fault, and keeping the other
 * six distinct is what stops a report full of correct dependencies from looking
 * like a report full of breakage.
 */
export type DependencyStatus =
  | "local"
  | "provided"
  | "system"
  | "external"
  | "missing"
  | "inferred"
  | "constraint";

export type DependencyNode = {
  /** Lower-cased. */
  id: string;
  /** The spelling to show: the project's own, or the manifest's. */
  label: string;
  status: DependencyStatus;
  /** Where the claim came from, for "why is this here" in a graph UI. */
  evidence: DependencyEvidence[];
};

export type DependencyEvidence = {
  /** `dependency`, `provide`, `@resource include`, `exports[…]`, `TriggerEvent`… */
  via: string;
  /** The resource that made the claim. */
  from: string;
  span?: GtaSourceSpan;
};

export type DependencyEdge = {
  from: string;
  to: string;
  kind: "declared" | "provided" | "inferred";
};

export type DependencyGraph = {
  nodes: DependencyNode[];
  edges: DependencyEdge[];
  /** Strongly connected components with more than one member, smallest first. */
  cycles: string[][];
  /**
   * A load order, cyclic groups collapsed to their first member. `null` when the
   * graph is so tangled that any order would be a guess.
   */
  order: string[] | null;
  constraints: ManifestConstraint[];
};

// ─── Vehicles ────────────────────────────────────────────────────────────────

/**
 * Everything one vehicle's files say about it, gathered in one place.
 *
 * Grouping by vehicle rather than by file is the difference between "here are
 * nine errors" and "this vehicle's handlingId is misspelled, and here are the
 * eight things that stop working because of it".
 */
export type VehicleEntity = {
  /** Lower-cased `modelName`. */
  model: string;
  /** As written, for display. */
  modelRaw: string;
  gameName: string | null;
  txdName: string | null;
  handlingId: string | null;
  layout: string | null;
  audioNameHash: string | null;
  modkits: string[];
  sirenSettings: string | null;
  lightSettings: string | null;
  flags: string[];
  /** True when the name or the class marks it as emergency fleet. */
  emergency: boolean;
  span: GtaSourceSpan;
  findings: GtaFinding[];
};

/** `[id, kitName, vehiclesUsingIt]` — the row shape of the generated stock table. */
export type VanillaModkit = [id: number, kitName: string, vehicles: string[]];

/** Derived lookups over the stock modkit table, built once and shared. */
export type VanillaModkitIndex = {
  byId: Map<number, VanillaModkit[]>;
  kitNames: Set<string>;
  ids: Set<number>;
  vehicles: Set<string>;
};

// ─── Stream assets ───────────────────────────────────────────────────────────

/**
 * One name in the flat namespace FiveM streams into.
 *
 * There is no folder structure in that namespace: two resources that both ship
 * `police.yft` are shipping the same asset name, whatever directories they put
 * it in, and exactly one of them wins.
 */
export type StreamAsset = {
  /** Lower-cased `name.extension`. */
  basename: string;
  extension: string;
  /**
   * Every place it was found, resource-qualified.
   *
   * `resource` is the display name and is allowed to repeat: two packs both
   * called `police` are the case this whole type exists for. `resourceIdentity`
   * is what actually separates them — the collector dedupes on it, and without
   * it the second pack's copy of `police.yft` is discarded as a re-listing of
   * the first pack's, which deletes the collision before anything can report it.
   * `resourceOrigin` is what a finding prints so the reader knows which zip to
   * open.
   *
   * They are both optional and they degrade differently, which is the part worth
   * reading twice. `resourceIdentity` falls back to `resource`: a name is a
   * usable key when there is only one resource wearing it, so a collector that
   * knows nothing else still produces occurrences that behave exactly as they
   * did. `resourceOrigin` does not fall back and is simply absent when unknown,
   * because there is no honest stand-in for "which zip this came from" — a
   * consumer that filled it from `resource` would print "police (from police)"
   * and present a fabricated archive name to a user as provenance. Degrade to
   * the unqualified name instead; `streamAssets.ts` does, and `collect.ts`
   * argues the same rule at the point the field is filled in.
   */
  occurrences: {
    resource: string;
    resourceIdentity?: string;
    resourceOrigin?: string;
    path: string;
    size: number;
  }[];
  /** True when the base game already owns this exact name. */
  vanilla: boolean;
  /** The base asset when this is a high-LOD companion (`_hi` / `+hi`). */
  highLodOf?: string;
};

// ─── The vanilla name index ──────────────────────────────────────────────────

/**
 * The lazily-loaded list of every streamable asset name the base game owns.
 *
 * 238k names is not something to put in an app bundle for the sake of a check
 * most sessions never run, so the loader fetches and decompresses on first use
 * and every consumer takes the async cost once. See `vanillaAssetNames.ts`.
 */
export type VanillaAssetNameIndex = {
  version: number;
  source: string;
  /** Which game build produced it, so a report can say. */
  gameBuild: string | null;
  /**
   * The day the index was generated, `YYYY-MM-DD`, as the manifest recorded it.
   *
   * The build alone dates the *game*, not the extraction: a rebuild of the index
   * against the same build fixes names and changes nothing a reader can see. A
   * report that stamps only the build cannot tell a user whether the index that
   * cleared their pack is a week old or a year old, which is the one question
   * that decides whether to trust a "not a stock name" answer.
   *
   * Optional because an index may be constructed from a caller's own dump with
   * no manifest behind it; a report prints an em dash rather than invent a date.
   */
  generated?: string | null;
  size: number;
  /** `police.yft` — the full name including extension, case-insensitive. */
  has(nameWithExtension: string): boolean;
  /** `police` plus an optional extension filter. */
  hasBasename(name: string, extension?: string): boolean;
};

// ─── Reports ─────────────────────────────────────────────────────────────────

export type GtaCheck = { label: string; passed: number; total: number };

/**
 * The verdict a product surface renders as a headline.
 *
 * PackBench turns this into "Ready to export" / "Export possible, but 3
 * warnings" / "Fix required before export"; the desktop app turns it into the
 * colour of a banner. Computing it once here is what keeps those two from
 * disagreeing about the same folder.
 */
export type GtaVerdict = "clean" | "warnings" | "blocked";

export type GtaResourceReport = {
  name: string;
  /**
   * Carried through from `GtaResourceInput` so an export can tell two reports
   * of the same name apart. See the argument on `GtaResourceInput.identity`:
   * without these, a Markdown file with two `## police` headings and a CSV with
   * two `police` rows is the last place the collapse happens, after every rule
   * has already got it right.
   */
  identity?: string;
  origin?: string;
  kind: "fivem-resource" | "asset-folder" | "folder";
  verdict: GtaVerdict;
  manifest: ParsedManifest | null;
  vehicles: VehicleEntity[];
  dependencies: DependencyGraph | null;
  stream: StreamAsset[];
  checks: GtaCheck[];
  /** Resource-level findings. Per-vehicle ones live on the vehicle. */
  findings: GtaFinding[];
  /**
   * True when a check was skipped because its input was not supplied — the
   * vanilla index was not loaded, `carcols.meta` was not read. A report that
   * quietly skips half its rules and says nothing is the failure mode this
   * exists to prevent.
   */
  skipped: string[];
};
